import type { ReactNode } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * The console's form controls.
 *
 * Every catalog form is a flat object of scalars, so these wrap the shadcn
 * primitives with the label / description / error arrangement rather than each
 * form repeating it. The props are deliberately narrow: a field that needs
 * something these do not offer should use the primitives directly instead of
 * growing an option here that one caller sets.
 */

interface BaseProps {
  id: string;
  label: string;
  error?: string;
  description?: ReactNode;
  className?: string;
  disabled?: boolean;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  description,
  placeholder,
  type,
  className,
  disabled,
  onBlur,
}: BaseProps & {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  onBlur?: () => void;
}) {
  return (
    <Field className={className} data-invalid={!!error}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-invalid={!!error}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
}

/**
 * A number input that reports an empty box as `null` rather than `0`.
 *
 * The distinction matters on this schema: a null `thc_percent` means "not
 * tested", and a null `low_stock_threshold` means "no alert" — neither is zero,
 * and silently writing zero would invent a fact.
 */
export function NumberField({
  id,
  label,
  value,
  onChange,
  error,
  description,
  placeholder,
  className,
  disabled,
  min,
  max,
  step,
  suffix,
}: BaseProps & {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <Field className={className} data-invalid={!!error}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>

      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          value={value ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.target.value;
            onChange(raw === '' ? null : Number(raw));
          }}
          aria-invalid={!!error}
          className={cn(suffix && 'pr-10')}
        />

        {suffix && (
          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm">
            {suffix}
          </span>
        )}
      </div>

      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
}

export function TextareaField({
  id,
  label,
  value,
  onChange,
  error,
  description,
  placeholder,
  rows = 4,
  className,
  disabled,
}: BaseProps & {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <Field className={className} data-invalid={!!error}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Textarea
        id={id}
        rows={rows}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={!!error}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
}

export interface Option {
  value: string;
  label: string;
}

export function SelectField({
  id,
  label,
  options,
  value,
  onChange,
  error,
  description,
  placeholder,
  className,
  disabled,
}: BaseProps & {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <Field className={className} data-invalid={!!error}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>

      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {description && <FieldDescription>{description}</FieldDescription>}
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
}

/**
 * A boolean, laid out as a row rather than a stack.
 *
 * A switch is its own label position: putting the caption above it the way a
 * text input does leaves the control floating in whitespace with nothing to
 * associate it with.
 */
export function SwitchField({
  id,
  label,
  checked,
  onChange,
  description,
  className,
  disabled,
}: Omit<BaseProps, 'error'> & {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 rounded-lg border p-4', className)}>
      <div className="min-w-0">
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {description && <FieldDescription className="mt-1">{description}</FieldDescription>}
      </div>

      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/**
 * A checkbox with its label beside it.
 *
 * The label is `FieldLabel` like every other field's, so a checkbox caption is
 * the same size and weight as a text input's — the thing that most often drifts
 * when checkbox rows are hand-rolled per screen.
 *
 * `htmlFor` rather than wrapping: Radix renders a `button[role=checkbox]`, not
 * an `<input>`, so nesting alone associates nothing.
 */
export function CheckboxField({
  id,
  label,
  checked,
  onChange,
  description,
  className,
  disabled,
}: Omit<BaseProps, 'error'> & {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onChange(value === true)}
        className="mt-0.5"
      />

      <div className="min-w-0">
        <FieldLabel htmlFor={id} className="cursor-pointer">
          {label}
        </FieldLabel>
        {description && <FieldDescription className="mt-0.5">{description}</FieldDescription>}
      </div>
    </div>
  );
}

/** A titled band of fields inside a form. */
export function FormSection({
  title,
  description,
  children,
  columns = 2,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  columns?: 1 | 2;
}) {
  return (
    <section className="bg-card text-card-foreground flex flex-col gap-4 rounded-lg border p-5">
      <div className="flex flex-col gap-y-0.5">
        <h2 className="text-sm font-medium">{title}</h2>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>

      <div className={cn('grid gap-4', columns === 2 && 'sm:grid-cols-2')}>{children}</div>
    </section>
  );
}
